import Markdown from 'markdown-to-jsx';
import Link from 'next/link';
import { SignIn, SignUp, UserButton, UserProfile } from '@clerk/nextjs';
import { Code } from '@components/sanity/serializers/code';
import {
  Button,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  Box,
  UnorderedList,
  OrderedList,
  ListItem
} from '@chakra-ui/react';
import { Callout } from './callout';
import { useEffect, useState } from 'react';
import Image from 'next/image';
import darkTheme from 'prism-react-renderer/themes/oceanicNext';
import type { Language } from 'prism-react-renderer';

const CodeBlock = ({ className, children }: any) => {
  let lang = 'text';
  if (className && className.startsWith('lang-')) {
    lang = className.replace('lang-', '');
  }

  return (
    <Code
      theme={darkTheme}
      language={lang as Language}
      withCopy
      withLineNumbers
    >
      {children}
    </Code>
  );
};

// markdown-to-jsx uses <pre><code/></pre> for code blocks.
const PreBlock = ({ children, ...rest }: any) => {
  if ('type' in children && children['type'] === 'code') {
    return CodeBlock(children['props']);
  }
  return <pre {...rest}>{children}</pre>;
};

export function MDX({ content }: any) {
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (content) {
      setLoaded(true);
    }
  }, []);

  if (loaded) {
    return (
      <Markdown
        options={{
          overrides: {
            SignIn: {
              component: SignIn
            },
            SignUp: {
              component: SignUp
            },
            UserButton: {
              component: UserButton
            },
            UserProfile: {
              component: UserProfile
            },
            Callout({ children, ...props }: any) {
              return <Callout body={children} type={props.type} {...props} />;
            },
            button({ children, ...props }: any) {
              return (
                <Button
                  as="button"
                  onClick={() => eval(props?.onclick)}
                  {...props}
                >
                  {children}
                </Button>
              );
            },
            pre: PreBlock,
            a({ children, href }: any) {
              return <Link href={href}>{children}</Link>;
            },
            h1({ children, ...props }: any) {
              return (
                <Heading {...props} as="h1" fontSize="48px" pt="24px" pb="12px">
                  {children}
                </Heading>
              );
            },
            h2({ children, ...props }: any) {
              return (
                <Heading {...props} as="h2" fontSize="36px" pt="24px" pb="12px">
                  {children}
                </Heading>
              );
            },
            h3({ children, ...props }: any) {
              return (
                <Heading {...props} as="h3" fontSize="24px" pt="24px" pb="12px">
                  {children}
                </Heading>
              );
            },
            h4({ children, ...props }: any) {
              return (
                <Heading {...props} as="h4" fontSize="16px" py="12px">
                  {children}
                </Heading>
              );
            },
            h5({ children, ...props }: any) {
              return (
                <Heading {...props} as="h5" fontSize="12px" py="12px">
                  {children}
                </Heading>
              );
            },
            h6({ children, ...props }: any) {
              return (
                <Heading {...props} as="h6" fontSize="8px" py="12px">
                  {children}
                </Heading>
              );
            },
            table: Table,
            tbody: Tbody,
            thead: Thead,
            tfoot: Tfoot,
            th: Th,
            tr: Tr,
            td: Td,
            p({ children, ...props }: any) {
              return (
                <Text as="p" {...props} py="12px">
                  {children}
                </Text>
              );
            },
            div({ children, ...props }: any) {
              return (
                <Box as="div" {...props}>
                  {children}
                </Box>
              );
            },
            ul: UnorderedList,
            ol: OrderedList,
            li: ListItem,
            blockquote({ children }: any) {
              return (
                <Text
                  bg="#f9f9f9"
                  borderLeft="10px solid #ccc"
                  margin="1.5em 10px"
                  padding="0.5em 10px"
                >
                  <Box ml="12px">{children}</Box>
                </Text>
              );
            },
            img({ ...props }: any) {
              return (
                <Box w="100%" py="12px">
                  <Image
                    alt={props.alt}
                    src={props.src}
                    width={1000}
                    height={500}
                  />
                </Box>
              );
            }
          }
        }}
      >
        {content}
      </Markdown>
    );
  } else {
    return <></>;
  }
}
